import { useMemo } from 'react';
import { Box, Typography, Chip, Table, TableBody, TableCell, TableRow, TableHead } from '@mui/material';
import { extractValues, skewness, kurtosis } from '../statistics';
import { useTimeline } from '../TimelineContext';

export default function DistributionWidget({ recordings = [], recordingMetadata = {}, widgetConfig }) {
  const { window: timeWindow } = useTimeline();
  const recId1 = widgetConfig?.recId1 || '';
  const recId2 = widgetConfig?.recId2 || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec1 = recordings.find((r) => r.id === recId1);
  const rec2 = recordings.find((r) => r.id === recId2);

  const results = useMemo(() => {
    if (!sensorKey) return null;
    const compute = (rec) => {
      if (!rec) return null;
      const vals = extractValues(rec, sensorKey, timeWindow?.start, timeWindow?.end);
      if (vals.length < 4) return null;
      return { skewness: skewness(vals), kurtosis: kurtosis(vals), n: vals.length };
    };
    const r1 = compute(rec1);
    const r2 = compute(rec2);
    if (!r1 && !r2) return null;
    return { r1, r2 };
  }, [rec1, rec2, sensorKey, timeWindow]);

  const fmtVal = (v) => isNaN(v) ? '—' : v.toFixed(4);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {results ? (
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }}>Measure</TableCell>
                {results.r1 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{(rec1 && recordingMetadata[rec1.id]?.label) || rec1?.name || 'Rec 1'}</TableCell>}
                {results.r2 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem', fontWeight: 'bold' }} align="right">{(rec2 && recordingMetadata[rec2.id]?.label) || rec2?.name || 'Rec 2'}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Skewness</TableCell>
                {results.r1 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r1.skewness.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r1.skewness.interpretation}</Typography>
                  </TableCell>
                )}
                {results.r2 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r2.skewness.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r2.skewness.interpretation}</Typography>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Kurtosis</TableCell>
                {results.r1 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r1.kurtosis.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r1.kurtosis.interpretation}</Typography>
                  </TableCell>
                )}
                {results.r2 && (
                  <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">
                    {fmtVal(results.r2.kurtosis.value)}
                    <Typography variant="caption" display="block" color="text.secondary">{results.r2.kurtosis.interpretation}</Typography>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }}>Sample Size</TableCell>
                {results.r1 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">n={results.r1.n}</TableCell>}
                {results.r2 && <TableCell sx={{ py: 0.25, fontSize: '0.7rem' }} align="right">n={results.r2.n}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select recording(s) and a sensor
          </Typography>
        </Box>
      )}
    </Box>
  );
}
